import { watch } from 'vue';
import { Color as FabricColor } from 'fabric';
import { LAYER_PROPS, LAYER_TYPES } from '@/constants/layers';
import {
  IMAGE_PROPS,
  DEFAULT_SHADOW_COLOR,
  hasUsableUrlScheme
} from '@/constants/media';
import { DEFAULT_SHAPE_COLOR } from '@/constants/shapes';
import { DEFAULT_TEXT_COLOR, TEXT_PROPS } from '@/constants/text';
import type { LayerType } from '@/types/layers/layer';
import { i18n } from '@/plugins/i18n';
import { isKnownUnloadableMedia } from '@/services/media/mediaUrlProbe';
import { ATTRIBUTE_VALUE_TYPES, CHOICE_KEY_OPTIONS, COLOR_KEYS, BOOLEAN_STRINGS, FONT_WEIGHT_ALIASES, LANGUAGE_KEY, NUMBER_KEY_MIN, PRODUCT_ID_KEY, FONT_FAMILY_KEY } from '@/constants/attributeValueTypes';
import type { AttributeValueType, AttributeValue } from '@/types/feed/attribute';
import {
  DEFAULT_LANGUAGE_NAME,
  findLanguage,
  isLanguageRegistryEmpty,
  languageNameFor,
  onLanguageRegistryChange
} from '@/constants/languageRegistry';
import {
  isProductKnownInvalid,
  onProductRegistryChange
} from '@/constants/productRegistry';
import {
  parseFontValue,
  isKnownFontFamily,
  isFontRegistryEmpty,
  onFontRegistryChange
} from '@/helpers/fontValue';

const colorTestEl =
  typeof document === 'undefined' ? null : document.createElement('div');

export function isValidCssColor(value: string): boolean {
  if (!colorTestEl) return true;
  colorTestEl.style.color = '';
  colorTestEl.style.color = value;
  return colorTestEl.style.color !== '';
}

/**
 * Validation runs on the grid's hot path — AG Grid re-evaluates `cellClass`
 * and `tooltipValueGetter` for every rendered cell on each `refreshCells`,
 * which fires on every mouse-over while drag-selecting. Cell values repeat
 * heavily across a variant column, so memoise both entry points and let the
 * caches bound themselves.
 */
const MEMO_LIMIT = 1000;

function memoSet<V>(cache: Map<string, V>, key: string, value: V): V {
  if (cache.size >= MEMO_LIMIT) cache.clear();
  cache.set(key, value);
  return value;
}

/** Single source of truth: targetKey → value type. */
export function getAttributeValueType(targetKey: string): AttributeValueType {
  if (targetKey === LAYER_PROPS.VISIBLE || targetKey === LAYER_PROPS.LOCKED)
    return ATTRIBUTE_VALUE_TYPES.BOOLEAN;
  if (targetKey === LAYER_PROPS.OPACITY) return ATTRIBUTE_VALUE_TYPES.OPACITY;
  if (targetKey in NUMBER_KEY_MIN) return ATTRIBUTE_VALUE_TYPES.NUMBER;
  if (targetKey in CHOICE_KEY_OPTIONS) return ATTRIBUTE_VALUE_TYPES.CHOICE;
  if (targetKey === IMAGE_PROPS.URL) return ATTRIBUTE_VALUE_TYPES.URL;
  if (targetKey === 'videoUrl') return ATTRIBUTE_VALUE_TYPES.VIDEO_URL;
  if (targetKey === 'audioUrl') return ATTRIBUTE_VALUE_TYPES.AUDIO_URL;
  if (targetKey === LANGUAGE_KEY) return ATTRIBUTE_VALUE_TYPES.LANGUAGE;
  if (targetKey === PRODUCT_ID_KEY) return ATTRIBUTE_VALUE_TYPES.PRODUCT_ID;
  if (targetKey === FONT_FAMILY_KEY) return ATTRIBUTE_VALUE_TYPES.FONT_FAMILY;
  if (COLOR_KEYS.has(targetKey)) return ATTRIBUTE_VALUE_TYPES.COLOR;
  return ATTRIBUTE_VALUE_TYPES.STRING;
}

export function parseAttributeValue(
  targetKey: string,
  raw: unknown
): AttributeValue {
  const s = typeof raw === 'string' ? raw : String(raw ?? '');
  const type = getAttributeValueType(targetKey);
  switch (type) {
    case ATTRIBUTE_VALUE_TYPES.BOOLEAN: {
      return s.trim().toLowerCase() === 'true';
    }
    case ATTRIBUTE_VALUE_TYPES.OPACITY: {
      return clampOpacityPercent(s);
    }
    case ATTRIBUTE_VALUE_TYPES.NUMBER: {
      // anything unreadable stays verbatim so the grid can flag it
      const trimmed = s.trim();
      const n = Number(trimmed);
      return trimmed && Number.isFinite(n) ? n : s;
    }
    case ATTRIBUTE_VALUE_TYPES.CHOICE: {
      return canonicalChoice(targetKey, s) ?? s;
    }
    case ATTRIBUTE_VALUE_TYPES.COLOR: {
      if (/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(s)) return `#${s}`;
      if (s && CSS.supports('color', s)) {
        return `#${new FabricColor(s).toHex().toLowerCase()}`;
      }
      return s;
    }
    case ATTRIBUTE_VALUE_TYPES.LANGUAGE: {
      // Canonicalise a known name or ISO code to one spelling; leave anything
      // else verbatim for the grid to flag.
      return languageNameFor(s) ?? s;
    }
    default: {
      return s;
    }
  }
}

/**
 * The one spelling a choice cell is stored and drawn with — a font weight as a
 * number, any other choice as the option is written — or undefined when it is
 * not one of the key's options.
 */
export function canonicalChoice(
  targetKey: string,
  value: unknown
): AttributeValue | undefined {
  const s = String(value ?? '')
    .trim()
    .toLowerCase();
  if (targetKey === TEXT_PROPS.FONT_WEIGHT) {
    const weight = FONT_WEIGHT_ALIASES[s] ?? s;
    return CHOICE_KEY_OPTIONS[targetKey]?.includes(String(weight))
      ? Number(weight)
      : undefined;
  }
  return CHOICE_KEY_OPTIONS[targetKey]?.find(
    option => option.toLowerCase() === s
  );
}

export function defaultValueForTargetKey(targetKey: string): AttributeValue {
  if (targetKey === LAYER_PROPS.OPACITY) return 100;
  if (targetKey === LAYER_PROPS.VISIBLE) return true;
  if (targetKey === LANGUAGE_KEY) return DEFAULT_LANGUAGE_NAME;
  if (targetKey === FONT_FAMILY_KEY) {
    return '';
  }
  return parseAttributeValue(targetKey, '');
}

// fill, stroke and shadow all fall back to their layer type's own default
const DEFAULT_COLOR_BY_LAYER_TYPE: Partial<Record<LayerType, string>> = {
  [LAYER_TYPES.TEXT]: DEFAULT_TEXT_COLOR,
  [LAYER_TYPES.SHAPE]: DEFAULT_SHAPE_COLOR,
  [LAYER_TYPES.ELEMENT]: DEFAULT_SHAPE_COLOR,
  [LAYER_TYPES.IMAGE]: DEFAULT_SHADOW_COLOR
};

/**
 * `ownValue` is what the layer shows on this ad without the binding; a blank
 * or unreadable number cell falls back to it.
 */
export function resolveLayerValue(
  targetKey: string,
  value: AttributeValue,
  layerType: LayerType,
  ownValue?: unknown
): unknown {
  if (targetKey === LAYER_PROPS.OPACITY) {
    return clampOpacityPercent(stringifyAttributeValue(value)) / 100;
  }
  if (getAttributeValueType(targetKey) === ATTRIBUTE_VALUE_TYPES.NUMBER) {
    const isBlank = String(value ?? '').trim() === '';
    if (isBlank || getCellValidationError(value, targetKey)) return ownValue;
    const n = Number(value);
    return targetKey === LAYER_PROPS.ANGLE ? ((n % 360) + 360) % 360 : n;
  }
  if (getAttributeValueType(targetKey) === ATTRIBUTE_VALUE_TYPES.CHOICE) {
    return canonicalChoice(targetKey, value) ?? ownValue;
  }
  if (getAttributeValueType(targetKey) === ATTRIBUTE_VALUE_TYPES.BOOLEAN) {
    // an unreadable flag keeps the default (visible, unlocked) instead of hiding the layer
    return getCellValidationError(value, targetKey)
      ? defaultValueForTargetKey(targetKey)
      : parseAttributeValue(targetKey, value);
  }
  // blank leaves fabric with no paint and a flagged colour reaches it as garbage,
  // so both fall back to the layer's own default
  if (COLOR_KEYS.has(targetKey)) {
    const isBlank = value == null || String(value).trim() === '';
    if (isBlank || getCellValidationError(value, targetKey)) {
      // a text outline is optional, so falling back to the black text default would draw one
      if (targetKey === TEXT_PROPS.STROKE && layerType === LAYER_TYPES.TEXT) {
        return '';
      }
      return DEFAULT_COLOR_BY_LAYER_TYPE[layerType] ?? value;
    }
  }
  if (targetKey === FONT_FAMILY_KEY) {
    const strVal = String(value ?? '').trim();
    if (!strVal) return '';
    const { family } = parseFontValue(strVal);
    if (getCellValidationError(value, targetKey) !== null) return '';
    return family;
  }
  return value;
}

/**
 * A whole percent stored as a fraction reads back a hair low — 29 / 100 * 100 is
 * 28.999999999999996 — so nudge before flooring or 29, 57 and 58 each lose a point.
 */
const OPACITY_EPSILON = 1e-9;

/**
 * Opacity has no error state: unreadable text and negatives land on 0, anything
 * above 100 on 100. Decimals floor to match the integer-only toolbar slider.
 */
export function clampOpacityPercent(raw: string | number): number {
  const trimmed = String(raw).trim();
  // Blank is "not set", which means fully opaque - not an invisible layer.
  if (!trimmed) return 100;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.floor(n + OPACITY_EPSILON)));
}

/** Inverse of {@link clampOpacityPercent}: a stored 0–1 fraction back to a whole percent. */
export function opacityPercentFromFraction(fraction: number): number {
  return clampOpacityPercent(fraction * 100);
}

/** Gates converting a column to opacity, where a column of text would collapse to zeroes. */
function isNumericValue(value: AttributeValue): boolean {
  return Number.isFinite(Number(stringifyAttributeValue(value).trim()));
}

export function stringifyAttributeValue(v: AttributeValue): string {
  return typeof v === 'string' ? v : String(v);
}

function isValidRgbColor(trimmed: string): boolean {
  const match = trimmed.match(/^rgba?\(\s*([^)]+)\s*\)$/);
  if (!match?.[1]) return false;
  // `/` separates the alpha in modern space-separated syntax: rgb(0 0 0 / 50%)
  const parts = match[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return false;

  for (let i = 0; i < 3; i++) {
    const p = parts[i];
    if (!p) return false;
    const isPercent = p.endsWith('%');
    const num = isPercent ? Number(p.slice(0, -1)) : Number(p);
    const max = isPercent ? 100 : 255;
    if (Number.isNaN(num) || num < 0 || num > max) return false;
  }

  if (parts.length === 4) {
    const p = parts[3];
    if (!p) return false;
    const isPercent = p.endsWith('%');
    const num = isPercent ? Number(p.slice(0, -1)) : Number(p);
    const max = isPercent ? 100 : 1;
    if (Number.isNaN(num) || num < 0 || num > max) return false;
  }

  return true;
}

function isValidHslColor(trimmed: string): boolean {
  const match = trimmed.match(/^hsla?\(\s*([^)]+)\s*\)$/);
  if (!match?.[1]) return false;
  const parts = match[1].split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return false;

  const sat = parts[1];
  const light = parts[2];
  if (!sat || !light) return false;

  const satNum = sat.endsWith('%') ? Number(sat.slice(0, -1)) : Number(sat);
  if (Number.isNaN(satNum) || satNum < 0 || satNum > 100) return false;

  const lightNum = light.endsWith('%')
    ? Number(light.slice(0, -1))
    : Number(light);
  if (Number.isNaN(lightNum) || lightNum < 0 || lightNum > 100) return false;

  if (parts.length === 4) {
    const p = parts[3];
    if (!p) return false;
    const isPercent = p.endsWith('%');
    const alphaNum = isPercent ? Number(p.slice(0, -1)) : Number(p);
    const maxAlpha = isPercent ? 100 : 1;
    if (Number.isNaN(alphaNum) || alphaNum < 0 || alphaNum > maxAlpha)
      return false;
  }

  return true;
}

const colorCache = new Map<string, boolean>();

export function isValidColorString(value: string): boolean {
  const cached = colorCache.get(value);
  if (cached !== undefined) return cached;

  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return memoSet(colorCache, value, false);

  if (trimmed.startsWith('rgb(') || trimmed.startsWith('rgba(')) {
    return memoSet(colorCache, value, isValidRgbColor(trimmed));
  }

  if (trimmed.startsWith('hsl(') || trimmed.startsWith('hsla(')) {
    return memoSet(colorCache, value, isValidHslColor(trimmed));
  }

  return memoSet(colorCache, value, isValidCssColor(trimmed));
}

const HEX_WITHOUT_HASH_REGEX =
  /^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const validationCache = new Map<string, string | null>();

// Without this, a "valid" decided against the empty registry is served forever
// and every language cell stays unflagged once the real list arrives.
onLanguageRegistryChange(() => validationCache.clear());

// Same for a product id: it validates until its lookup comes back.
onProductRegistryChange(() => validationCache.clear());

onFontRegistryChange(() => validationCache.clear());

// The cache holds rendered message text, so a locale switch has to drop it.
watch(
  () => i18n.global.locale.value,
  () => validationCache.clear()
);

/**
 * Human-readable reason a cell's value is unusable, or `null` when it is fine.
 * Every type but STRING and opacity can fail; opacity clamps instead.
 */
export function getCellValidationError(
  value: AttributeValue,
  targetKey: string
): string | null {
  if (value === '' || value == null) return null;

  // Not memoised: the verdict flips the moment a probe lands.
  if (isMediaValueType(getAttributeValueType(targetKey))) {
    return mediaValidationError(String(value));
  }

  const cacheKey = `${targetKey}\u0000${String(value)}`;
  const cached = validationCache.get(cacheKey);
  if (cached !== undefined) return cached;

  return memoSet(
    validationCache,
    cacheKey,
    computeCellValidationError(value, targetKey)
  );
}

function isMediaValueType(type: AttributeValueType): boolean {
  return (
    type === ATTRIBUTE_VALUE_TYPES.URL ||
    type === ATTRIBUTE_VALUE_TYPES.VIDEO_URL ||
    type === ATTRIBUTE_VALUE_TYPES.AUDIO_URL
  );
}

/** Two failures that read differently: never a URL, and a URL that would not load. */
function mediaValidationError(value: string): string | null {
  const strVal = value.trim();
  if (!strVal) return null;
  if (!hasUsableUrlScheme(strVal)) {
    return i18n.global.t('errors.tools.attributeTool.invalidMediaUrl');
  }
  if (isKnownUnloadableMedia(strVal)) {
    return i18n.global.t('errors.tools.attributeTool.mediaFailedToLoad');
  }
  return null;
}

function computeCellValidationError(
  value: AttributeValue,
  targetKey: string
): string | null {
  const type = getAttributeValueType(targetKey);

  switch (type) {
    case ATTRIBUTE_VALUE_TYPES.COLOR: {
      const strVal = String(value).trim();
      if (!strVal) return null;
      if (isValidColorString(strVal)) return null;
      if (HEX_WITHOUT_HASH_REGEX.test(strVal)) {
        return i18n.global.t(
          'errors.tools.attributeTool.colorHexMustStartWithHash'
        );
      }
      if (strVal.startsWith('#')) {
        return i18n.global.t(
          'errors.tools.attributeTool.invalidHexColorFormat'
        );
      }
      return i18n.global.t('errors.tools.attributeTool.invalidColorFormat');
    }
    case ATTRIBUTE_VALUE_TYPES.BOOLEAN: {
      if (typeof value === 'boolean') return null;
      const strVal = String(value).trim();
      if (!strVal) return null;
      if (BOOLEAN_STRINGS.has(strVal.toLowerCase())) return null;
      return i18n.global.t('errors.tools.attributeTool.invalidBoolean');
    }
    case ATTRIBUTE_VALUE_TYPES.LANGUAGE: {
      const strVal = String(value).trim();
      if (!strVal) return null;
      // Flagging a whole column the user cannot fix yet is worse than flagging
      // it a moment late, so an unloaded list accepts everything.
      if (isLanguageRegistryEmpty()) return null;
      if (findLanguage(strVal)) return null;
      return i18n.global.t('errors.tools.attributeTool.invalidLanguage');
    }
    case ATTRIBUTE_VALUE_TYPES.PRODUCT_ID: {
      const strVal = String(value).trim();
      if (!strVal) return null;
      // Only a lookup that came back empty flags; anything else is unknown yet.
      if (!isProductKnownInvalid(strVal)) return null;
      return i18n.global.t('errors.tools.attributeTool.invalidProductId');
    }
    case ATTRIBUTE_VALUE_TYPES.NUMBER: {
      const strVal = String(value).trim();
      if (!strVal) return null;
      const n = Number(strVal);
      if (!Number.isFinite(n)) {
        return i18n.global.t('errors.tools.attributeTool.invalidNumber');
      }
      const min = NUMBER_KEY_MIN[targetKey];
      if (min !== undefined && n < min) {
        return i18n.global.t('errors.tools.attributeTool.numberBelowMin', {
          min
        });
      }
      return null;
    }
    case ATTRIBUTE_VALUE_TYPES.CHOICE: {
      if (!String(value).trim()) return null;
      if (canonicalChoice(targetKey, value) !== undefined) return null;
      return i18n.global.t('errors.tools.attributeTool.invalidChoice', {
        values: CHOICE_KEY_OPTIONS[targetKey]?.join(', ') ?? ''
      });
    }
    case ATTRIBUTE_VALUE_TYPES.FONT_FAMILY: {
      const strVal = String(value).trim();
      if (!strVal) return null;
      if (isFontRegistryEmpty()) return null;
      const { family } = parseFontValue(strVal);
      if (isKnownFontFamily(family)) return null;
      return i18n.global.t('errors.tools.attributeTool.invalidFontFamily');
    }
    default: {
      return null;
    }
  }
}

/**
 * Can this value survive being reinterpreted as `targetKey`? Gates converting
 * a whole column. Every type but opacity delegates to
 * {@link getCellValidationError}, so the gate and the in-grid marker cannot
 * disagree about a cell.
 */
export function isValidForTargetKey(
  value: AttributeValue,
  targetKey: string
): boolean {
  if (value === '' || value == null) return true;
  const type = getAttributeValueType(targetKey);
  switch (type) {
    case ATTRIBUTE_VALUE_TYPES.BOOLEAN: {
      return getCellValidationError(value, targetKey) === null;
    }
    case ATTRIBUTE_VALUE_TYPES.OPACITY: {
      // Stricter than the grid: one unreadable cell clamps, a whole column of
      // them is a conversion nobody meant.
      return isNumericValue(value);
    }
    case ATTRIBUTE_VALUE_TYPES.NUMBER:
    case ATTRIBUTE_VALUE_TYPES.CHOICE:
    case ATTRIBUTE_VALUE_TYPES.URL:
    case ATTRIBUTE_VALUE_TYPES.VIDEO_URL:
    case ATTRIBUTE_VALUE_TYPES.AUDIO_URL:
    case ATTRIBUTE_VALUE_TYPES.COLOR:
    case ATTRIBUTE_VALUE_TYPES.LANGUAGE:
    case ATTRIBUTE_VALUE_TYPES.FONT_FAMILY: {
      return getCellValidationError(value, targetKey) === null;
    }
    case ATTRIBUTE_VALUE_TYPES.STRING: {
      return true;
    }
    default: {
      return true;
    }
  }
}

/**
 * Parses a value entering a cell, keeping invalid input verbatim so the grid
 * can flag it rather than coercing the mistake away. Opacity is the exception:
 * no error state, so it clamps on the way in.
 */
export function parseCellInput(
  targetKey: string,
  raw: unknown
): AttributeValue {
  const s = typeof raw === 'string' ? raw : String(raw ?? '');
  /**
   * Colour parsing repairs rather than coerces — it prepends the '#' a bare hex
   * is missing and returns anything else untouched — so it must run before the
   * validity check, or a bare hex would be flagged instead of fixed. Unusable
   * colours still come back verbatim and still get flagged.
   */
  if (getAttributeValueType(targetKey) === ATTRIBUTE_VALUE_TYPES.COLOR) {
    return parseAttributeValue(targetKey, s);
  }
  if (getCellValidationError(s, targetKey) !== null) return s;
  return parseAttributeValue(targetKey, s);
}

export function normalizeVariantMatrix(
  variants: readonly unknown[][],
  attributes: Iterable<{ columnIndex: number; targetKey: string }>
): AttributeValue[][] {
  const columnKeys: string[] = [];
  for (const attr of attributes) {
    columnKeys[attr.columnIndex] = attr.targetKey;
  }
  // same rules as paste, so a saved feed's bad value is flagged rather than coerced
  return variants.map(row =>
    row.map((cell, col) => parseCellInput(columnKeys[col] ?? '', cell))
  );
}
